import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  increment,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from './config';
import { BlogPost, BlogCategory, BlogTag, BlogComment, BlogStats } from '../types';
import { submitToSearchEngines } from './searchEngineService';

class BlogService {
  private postsCollection = collection(db, 'blog-posts');
  private categoriesCollection = collection(db, 'blog-categories');
  private tagsCollection = collection(db, 'blog-tags');
  private commentsCollection = collection(db, 'blog-comments');

  // Convert Firestore timestamp to Date with safety
  private convertTimestamp(timestamp: any): Date {
    if (!timestamp) return new Date();
    if (typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
    }
    if (timestamp instanceof Date) {
      return timestamp;
    }
    if (typeof timestamp === 'number') {
      return new Date(timestamp);
    }
    if (typeof timestamp === 'string') {
      return new Date(timestamp);
    }
    return new Date();
  }

  // Convert Date to Firestore timestamp
  private convertToTimestamp(date: Date): Timestamp {
    return Timestamp.fromDate(date);
  }

  // Generate slug from title
  private generateSlug(title: string): string {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9 -]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  }

  // Calculate read time based on content
  private calculateReadTime(content: string): number {
    const wordsPerMinute = 200;
    const wordCount = content.split(/\s+/).length;
    return Math.ceil(wordCount / wordsPerMinute);
  }

  // Create a new blog post
  async createPost(postData: Omit<BlogPost, 'id' | 'createdAt' | 'updatedAt' | 'views' | 'slug' | 'readTime'> & { slug?: string; faqs?: { question: string; answer: string }[] }): Promise<string> {
    try {
      const slug = postData.slug || this.generateSlug(postData.title);
      const readTime = this.calculateReadTime(postData.content);

      // Filter out undefined values to prevent Firestore errors
      const cleanPostData = Object.fromEntries(
        Object.entries(postData).filter(([_, value]) => value !== undefined)
      );

      const publishedAt = postData.status === 'published' 
        ? (postData.publishedAt ? this.convertToTimestamp(postData.publishedAt) : serverTimestamp())
        : null;

      const post = {
        ...cleanPostData,
        slug,
        readTime,
        views: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        publishedAt
      };

      const docRef = await addDoc(this.postsCollection, post);

      // Trigger SEO indexing if published and NOT in the future
      if (postData.status === 'published') {
        const actualPublishedAt = postData.publishedAt ? new Date(postData.publishedAt) : new Date();
        if (actualPublishedAt <= new Date()) {
          const postWithId = {
            ...cleanPostData,
            id: docRef.id,
            slug,
            readTime,
            views: 0,
            createdAt: new Date(),
            updatedAt: new Date(),
            publishedAt: actualPublishedAt,
          } as BlogPost;
          await this.triggerSEOIndexing(docRef.id, postWithId);
        }
      }

      return docRef.id;
    } catch (error) {
      console.error('Error creating blog post:', error);
      throw new Error('Failed to create blog post');
    }
  }

  // Trigger SEO indexing for published posts
  private async triggerSEOIndexing(postId: string, post: BlogPost): Promise<void> {
    try {
      console.log(`Triggering SEO indexing for post: ${post.title}`);

      // Submit to search engines
      await submitToSearchEngines([
        `https://moizwp.com/blog/${post.slug}`
      ]);

      // Trigger Netlify build hook to update pre-rendered pages
      await this.triggerNetlifyBuild();

      console.log(`SEO indexing completed for post: ${post.title}`);
    } catch (error) {
      console.error('Error triggering SEO indexing:', error);
    }
  }

  // Trigger Netlify build hook to update pre-rendered pages
  public async triggerNetlifyBuild(): Promise<void> {
    try {
      const buildHookUrl = process.env.REACT_APP_NETLIFY_BUILD_HOOK || 'https://api.netlify.com/build_hooks/69f1678ed107034a32118565';
      
      console.log('Triggering Netlify build hook...');
      const response = await fetch(buildHookUrl, {
        method: 'POST',
        body: JSON.stringify({
          clear_cache: false
        })
      });

      if (response.ok) {
        console.log('Netlify build triggered successfully');
      } else {
        console.warn('Failed to trigger Netlify build:', response.statusText);
      }
    } catch (error) {
      console.error('Error triggering Netlify build:', error);
    }
  }

  // Get a single blog post by ID
  async getPost(id: string): Promise<BlogPost | null> {
    try {
      const docRef = doc(this.postsCollection, id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        const post = {
          id: docSnap.id,
          ...data,
          createdAt: this.convertTimestamp(data.createdAt),
          updatedAt: this.convertTimestamp(data.updatedAt),
          publishedAt: data.publishedAt ? this.convertTimestamp(data.publishedAt) : undefined
        } as BlogPost;

        // Check if scheduled
        if (post.status === 'published' && post.publishedAt && post.publishedAt > new Date()) {
          // If it's a direct getPost, we might want to allow it for admin previews
          // but for general safety, we filter it if not careful. 
          // However, getPost is often used in admin too.
          // Let's check status manually.
          return post; // Admin should see it
        }

        return post;
      }
      return null;
    } catch (error) {
      console.error('Error getting blog post:', error);
      throw new Error('Failed to get blog post');
    }
  }

  // Get a blog post by slug
  async getPostBySlug(slug: string): Promise<BlogPost | null> {
    try {
      // First try to get it as a published post (works for everyone according to Firestore rules)
      let q = query(this.postsCollection, where('slug', '==', slug), where('status', '==', 'published'));
      let querySnapshot;
      
      try {
        querySnapshot = await getDocs(q);
      } catch (error) {
        console.error('Error getting published post by slug:', error);
        return null;
      }

      // If not found, try without status filter (for admins previewing drafts)
      if (querySnapshot.empty) {
        q = query(this.postsCollection, where('slug', '==', slug));
        try {
          querySnapshot = await getDocs(q);
        } catch (e) {
          // Normal users will get a permission denied error here, which is expected
          // just ignore it since the post doesn't exist or is a draft they can't see
          return null;
        }
      }

      if (!querySnapshot.empty) {
        const docSnap = querySnapshot.docs[0];
        const data = docSnap.data();
        const post = {
          id: docSnap.id,
          ...data,
          createdAt: this.convertTimestamp(data.createdAt),
          updatedAt: this.convertTimestamp(data.updatedAt),
          publishedAt: data.publishedAt ? this.convertTimestamp(data.publishedAt) : undefined
        } as BlogPost;

        // Filter out scheduled posts for public view
        if (post.status === 'published' && post.publishedAt && post.publishedAt > new Date()) {
          // If the query was purely for 'published' posts, we should hide it
          // But wait, getPostBySlug tries published first, then all.
          // If it found a published one that is in the future, return null.
          return null;
        }

        return post;
      }
      return null;
    } catch (error) {
      console.error('Error getting blog post by slug:', error);
      throw new Error('Failed to get blog post');
    }
  }

  // Update a blog post
  async updatePost(id: string, updates: Partial<BlogPost>): Promise<void> {
    try {
      const docRef = doc(this.postsCollection, id);

      // Filter out undefined values to prevent Firestore errors
      const cleanUpdates = Object.fromEntries(
        Object.entries(updates).filter(([_, value]) => value !== undefined)
      );

      const updateData = {
        ...cleanUpdates,
        updatedAt: serverTimestamp(),
        readTime: updates.content ? this.calculateReadTime(updates.content) : undefined,
        slug: updates.slug || (updates.title ? this.generateSlug(updates.title) : undefined),
        publishedAt: updates.status === 'published' 
          ? (updates.publishedAt ? this.convertToTimestamp(updates.publishedAt instanceof Date ? updates.publishedAt : new Date(updates.publishedAt)) : (updates.publishedAt === undefined ? serverTimestamp() : null))
          : (updates.publishedAt instanceof Date ? this.convertToTimestamp(updates.publishedAt) : updates.publishedAt)
      };

      // Filter out undefined values from updateData as well
      const finalUpdateData = Object.fromEntries(
        Object.entries(updateData).filter(([_, value]) => value !== undefined)
      );

      await updateDoc(docRef, finalUpdateData);

      // Trigger SEO indexing if status changed to published and it's NOT in the future
      if (updates.status === 'published') {
        const post = await this.getPost(id);
        if (post && (!post.publishedAt || post.publishedAt <= new Date())) {
          await this.triggerSEOIndexing(id, post);
        }
      }
    } catch (error) {
      console.error('Error updating blog post:', error);
      throw new Error('Failed to update blog post');
    }
  }

  // Delete a blog post
  async deletePost(id: string): Promise<void> {
    try {
      const docRef = doc(this.postsCollection, id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting blog post:', error);
      throw new Error('Failed to delete blog post');
    }
  }

  // Get all blog posts with pagination
  async getPosts(
    status: 'draft' | 'published' | 'archived' | 'all' = 'published',
    limitCount: number = 10,
    page: number = 1
  ): Promise<{ posts: BlogPost[]; total: number; totalPages: number }> {
    try {
      let q = query(this.postsCollection);

      if (status !== 'all') {
        q = query(q, where('status', '==', status));
      }

      const now = new Date();
      const allPosts: BlogPost[] = [];

      q = query(q, orderBy('publishedAt', 'desc'));
      const querySnapshot = await getDocs(q);

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const post = {
          id: doc.id,
          ...data,
          createdAt: this.convertTimestamp(data.createdAt),
          updatedAt: this.convertTimestamp(data.updatedAt),
          publishedAt: data.publishedAt ? this.convertTimestamp(data.publishedAt) : undefined
        } as BlogPost;

        // Filter out scheduled posts for public view
        if (status === 'published' && post.publishedAt && post.publishedAt > now) {
          return;
        }

        allPosts.push(post);
      });

      const total = allPosts.length;
      const totalPages = Math.ceil(total / limitCount);

      // Apply pagination
      const startIndex = (page - 1) * limitCount;
      const endIndex = startIndex + limitCount;
      const posts = allPosts.slice(startIndex, endIndex);

      return { posts, total, totalPages };
    } catch (error) {
      console.error('Error getting blog posts:', error);
      throw new Error('Failed to get blog posts');
    }
  }

  // Get posts by category
  async getPostsByCategory(category: string, limitCount: number = 10): Promise<BlogPost[]> {
    try {
      // If category is empty, return all published posts
      if (!category || category.trim() === '') {
        const result = await this.getPosts('published', limitCount);
        return result.posts;
      }

      // For large limits, get all posts and filter client-side
      if (limitCount > 100) {
        const result = await this.getPosts('published', 1000);
        const allPosts = result.posts;
        const filteredPosts = allPosts.filter(post => post.category === category);
        return filteredPosts.slice(0, limitCount);
      }

      const q = query(
        this.postsCollection,
        where('category', '==', category),
        where('status', '==', 'published'),
        orderBy('publishedAt', 'desc'),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      const posts: BlogPost[] = [];

      const now = new Date();
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const post = {
          id: doc.id,
          ...data,
          createdAt: this.convertTimestamp(data.createdAt),
          updatedAt: this.convertTimestamp(data.updatedAt),
          publishedAt: data.publishedAt ? this.convertTimestamp(data.publishedAt) : undefined
        } as BlogPost;

        // Filter out scheduled posts
        if (post.publishedAt && post.publishedAt > now) {
          return;
        }

        posts.push(post);
      });

      return posts;
    } catch (error) {
      console.error('Error getting posts by category:', error);
      throw new Error('Failed to get posts by category');
    }
  }

  // Get posts by tag
  async getPostsByTag(tag: string, limitCount: number = 10): Promise<BlogPost[]> {
    try {
      // If tag is empty, return all published posts
      if (!tag || tag.trim() === '') {
        const result = await this.getPosts('published', limitCount);
        return result.posts;
      }

      // For large limits, get all posts and filter client-side
      if (limitCount > 100) {
        const result = await this.getPosts('published', 1000);
        const allPosts = result.posts;
        const filteredPosts = allPosts.filter(post => post.tags.includes(tag));
        return filteredPosts.slice(0, limitCount);
      }

      const q = query(
        this.postsCollection,
        where('tags', 'array-contains', tag),
        where('status', '==', 'published'),
        orderBy('publishedAt', 'desc'),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      const posts: BlogPost[] = [];

      const now = new Date();
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const post = {
          id: doc.id,
          ...data,
          createdAt: this.convertTimestamp(data.createdAt),
          updatedAt: this.convertTimestamp(data.updatedAt),
          publishedAt: data.publishedAt ? this.convertTimestamp(data.publishedAt) : undefined
        } as BlogPost;

        // Filter out scheduled posts
        if (post.publishedAt && post.publishedAt > now) {
          return;
        }

        posts.push(post);
      });

      return posts;
    } catch (error) {
      console.error('Error getting posts by tag:', error);
      throw new Error('Failed to get posts by tag');
    }
  }

  // Increment view count
  async incrementViews(id: string): Promise<void> {
    try {
      const docRef = doc(this.postsCollection, id);
      await updateDoc(docRef, {
        views: increment(1)
      });
    } catch (error) {
      console.error('Error incrementing views:', error);
    }
  }

  // Search posts
  async searchPosts(searchTerm: string, limitCount: number = 10): Promise<BlogPost[]> {
    try {
      // If search term is empty, return all published posts
      if (!searchTerm || searchTerm.trim() === '') {
        const result = await this.getPosts('published', limitCount);
        return result.posts;
      }

      // For large limits, get all posts and filter client-side
      if (limitCount > 100) {
        const result = await this.getPosts('published', 1000);
        const allPosts = result.posts;

        // Simple search in title, content, and tags
        const searchLower = searchTerm.toLowerCase();
        const filteredPosts = allPosts.filter(post =>
          post.title.toLowerCase().includes(searchLower) ||
          post.content.toLowerCase().includes(searchLower) ||
          post.tags.some(tag => tag.toLowerCase().includes(searchLower))
        );

        return filteredPosts.slice(0, limitCount);
      }

      // Note: Firestore doesn't support full-text search natively
      // This is a simple implementation - in production, consider using Algolia or similar
      const q = query(
        this.postsCollection,
        where('status', '==', 'published'),
        orderBy('publishedAt', 'desc'),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      const posts: BlogPost[] = [];

      const now = new Date();
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const post = {
          id: doc.id,
          ...data,
          createdAt: this.convertTimestamp(data.createdAt),
          updatedAt: this.convertTimestamp(data.updatedAt),
          publishedAt: data.publishedAt ? this.convertTimestamp(data.publishedAt) : undefined
        } as BlogPost;

        // Filter out scheduled posts
        if (post.publishedAt && post.publishedAt > now) {
          return;
        }

        // Simple search in title, content, and tags
        const searchLower = searchTerm.toLowerCase();
        if (
          post.title.toLowerCase().includes(searchLower) ||
          post.content.toLowerCase().includes(searchLower) ||
          post.tags.some(tag => tag.toLowerCase().includes(searchLower))
        ) {
          posts.push(post);
        }
      });

      return posts;
    } catch (error) {
      console.error('Error searching posts:', error);
      throw new Error('Failed to search posts');
    }
  }

  // Category operations
  async createCategory(categoryData: Omit<BlogCategory, 'id' | 'postCount'>): Promise<string> {
    try {
      const category = {
        ...categoryData,
        postCount: 0
      };

      const docRef = await addDoc(this.categoriesCollection, category);
      return docRef.id;
    } catch (error) {
      console.error('Error creating category:', error);
      throw new Error('Failed to create category');
    }
  }

  async getCategories(): Promise<BlogCategory[]> {
    try {
      const querySnapshot = await getDocs(this.categoriesCollection);
      const categories: BlogCategory[] = [];

      querySnapshot.forEach((doc) => {
        categories.push({
          id: doc.id,
          ...doc.data()
        } as BlogCategory);
      });

      return categories;
    } catch (error) {
      console.error('Error getting categories:', error);
      throw new Error('Failed to get categories');
    }
  }

  // Tag operations
  async createTag(tagData: Omit<BlogTag, 'id' | 'postCount'>): Promise<string> {
    try {
      const tag = {
        ...tagData,
        postCount: 0
      };

      const docRef = await addDoc(this.tagsCollection, tag);
      return docRef.id;
    } catch (error) {
      console.error('Error creating tag:', error);
      throw new Error('Failed to create tag');
    }
  }

  async getTags(): Promise<BlogTag[]> {
    try {
      const querySnapshot = await getDocs(this.tagsCollection);
      const tags: BlogTag[] = [];

      querySnapshot.forEach((doc) => {
        tags.push({
          id: doc.id,
          ...doc.data()
        } as BlogTag);
      });

      return tags;
    } catch (error) {
      console.error('Error getting tags:', error);
      throw new Error('Failed to get tags');
    }
  }

  async updateTag(tagId: string, tagData: Partial<BlogTag>): Promise<void> {
    try {
      const docRef = doc(this.tagsCollection, tagId);
      await updateDoc(docRef, {
        ...tagData,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating tag:', error);
      throw new Error('Failed to update tag');
    }
  }

  async deleteTag(tagId: string): Promise<void> {
    try {
      const docRef = doc(this.tagsCollection, tagId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting tag:', error);
      throw new Error('Failed to delete tag');
    }
  }

  async updateCategory(categoryId: string, categoryData: Partial<BlogCategory>): Promise<void> {
    try {
      const docRef = doc(this.categoriesCollection, categoryId);
      await updateDoc(docRef, {
        ...categoryData,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating category:', error);
      throw new Error('Failed to update category');
    }
  }

  async deleteCategory(categoryId: string): Promise<void> {
    try {
      const docRef = doc(this.categoriesCollection, categoryId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting category:', error);
      throw new Error('Failed to delete category');
    }
  }

  // Comment operations
  async createComment(commentData: Omit<BlogComment, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const comment = {
        ...commentData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(this.commentsCollection, comment);
      return docRef.id;
    } catch (error) {
      console.error('Error creating comment:', error);
      throw new Error('Failed to create comment');
    }
  }

  async getComments(postId: string): Promise<BlogComment[]> {
    try {
      const q = query(
        this.commentsCollection,
        where('postId', '==', postId),
        where('status', '==', 'approved'),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const comments: BlogComment[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        comments.push({
          id: doc.id,
          ...data,
          createdAt: this.convertTimestamp(data.createdAt),
          updatedAt: this.convertTimestamp(data.updatedAt)
        } as BlogComment);
      });

      return comments;
    } catch (error) {
      console.error('Error getting comments:', error);
      throw new Error('Failed to get comments');
    }
  }

  // Get blog statistics
  async getBlogStats(): Promise<BlogStats> {
    try {
      const [publishedQuery, draftQuery, archivedQuery] = await Promise.all([
        getDocs(query(this.postsCollection, where('status', '==', 'published'))),
        getDocs(query(this.postsCollection, where('status', '==', 'draft'))),
        getDocs(query(this.postsCollection, where('status', '==', 'archived')))
      ]);

      const totalPosts = publishedQuery.size + draftQuery.size + archivedQuery.size;
      let totalViews = 0;
      let totalComments = 0;

      // Calculate total views
      publishedQuery.forEach((doc) => {
        const data = doc.data();
        totalViews += data.views || 0;
      });

      // Get total comments
      const commentsQuery = await getDocs(this.commentsCollection);
      totalComments = commentsQuery.size;

      return {
        totalPosts,
        totalViews,
        totalComments,
        publishedPosts: publishedQuery.size,
        draftPosts: draftQuery.size,
        archivedPosts: archivedQuery.size
      };
    } catch (error) {
      console.error('Error getting blog stats:', error);
      throw new Error('Failed to get blog stats');
    }
  }

  // Seed sample blog posts (useful for development)
  async seedSamplePosts(): Promise<void> {
    const samplePosts = [
      {
        title: 'Mastering WordPress Performance in 2026',
        content: 'Learn the latest techniques for optimizing your WordPress site for speed and core web vitals...',
        excerpt: 'Optimize your WordPress site for elite speed and core web vitals in 2026.',
        author: { name: 'Moiz Team', email: 'team@moizwp.com' },
        category: 'Web Development',
        tags: ['WordPress', 'Performance', 'SEO'],
        status: 'published' as const,
        featuredImage: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085'
      },
      {
        title: 'The Future of AI in Web Development',
        content: 'Generative AI is changing how we build and interact with websites. Discover the latest trends...',
        excerpt: 'Discover how Generative AI is transforming the landscape of modern web development.',
        author: { name: 'Moiz Team', email: 'team@moizwp.com' },
        category: 'Business',
        tags: ['AI', 'Future', 'Tech'],
        status: 'published' as const,
        featuredImage: 'https://images.unsplash.com/photo-1677442136019-21780ecad995'
      }
    ];

    for (const postData of samplePosts) {
      await this.createPost(postData);
    }
  }
}

const blogService = new BlogService();
export default blogService;
